import { resolveAdapter } from './adapters'
import { buildTermSheet, finalizeInfo } from './buildTermSheet'
import { toTermSheetInput, type TermSheetInput } from './input'
import { DEFAULT_PROFILE_ID } from './profiles'
import { findingsFor } from './severity'
import type { TermSheet, TermSheetDigest } from './types'

/**
 * Stamping — the single place term sheets are attached.
 *
 * Runs ONCE at the end of the public-data pipeline rather than inside each
 * lender's converter. That is the whole architecture: ~200 Aave/Compound forks
 * get correct sheets from the generic builder with zero per-fork work, and
 * only the ~13 exotic families need an adapter.
 */

export interface StampOptions {
  /** Unix seconds; injected so tests are deterministic. */
  now?: number
  /** Attach ranked `implications[]` from the severity model. Default true. */
  withImplications?: boolean
  /**
   * Derive `governance` / `oracle` / exposure quality from the rows' own
   * `oracleInfo` + `risk.breakdown`. Default true — set `false` only to test
   * the un-enriched builder in isolation.
   */
  enrich?: boolean
}

/**
 * Build sheets for one lender's rows on one chain.
 *
 * Siblings matter: the exposure set (`backedBy` / `acceptedCollateral`) is
 * derived by cross-referencing the OTHER rows of the same lender, so the whole
 * group has to be built together.
 */
export function buildTermSheetsForGroup(
  rows: Record<string, any>[],
  ctx: {
    lender?: string
    chainId?: string
    market?: Record<string, any>
    /** Item-level `fixedTerm` from `/lending/latest` — see `toTermSheetInput`. */
    fixedTerm?: Record<string, any>
  } = {},
  opts: StampOptions = {},
): Map<string, TermSheet> {
  const inputs: TermSheetInput[] = rows.map((r) => toTermSheetInput(r, ctx))
  const out = new Map<string, TermSheet>()

  // Governance / oracle / asset-quality all ride on the rows already — derive
  // the index once for the whole group so each sheet, and each of its exposure
  // items, can be enriched without a second fetch.
  const index =
    opts.enrich === false ? undefined : enrichmentIndexFromRows(rows)

  for (const input of inputs) {
    if (!input.marketUid) continue
    const adapter = resolveAdapter(input.lender)
    const sheet = buildTermSheet(input, {
      now: opts.now,
      siblings: inputs,
      patch: adapter?.build(input),
      profileId: adapter?.profileId(input) ?? DEFAULT_PROFILE_ID,
    })

    if (index) enrichTermSheet(sheet, index)
    if (opts.withImplications !== false) attachImplications(sheet)
    out.set(input.marketUid, sheet)
  }

  return out
}

/**
 * Fill `info.implications[]` from the severity model, most severe first.
 *
 * Derived rather than hand-written, so a newly integrated lender gets correct
 * warnings the moment its adapter sets the right structured fields — and a
 * warning can never contradict the numbers next to it.
 */
export function attachImplications(sheet: TermSheet): TermSheet {
  if (sheet.supply) {
    const found = findingsFor(sheet, 'supply')
      .filter((f) => f.severity !== 'info')
      .map((f) => f.message)
    if (found.length) sheet.supply.info.implications = found
  }
  if (sheet.borrow) {
    const found = findingsFor(sheet, 'borrow')
      .filter((f) => f.severity !== 'info')
      .map((f) => f.message)
    if (found.length) sheet.borrow.info.implications = found
  }
  return sheet
}

// ---------------------------------------------------------------------------
// Deriving the enrichment index FROM the rows
// ---------------------------------------------------------------------------

const BAND_ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

/**
 * Build an {@link EnrichmentIndex} from the market rows themselves.
 *
 * The governance and oracle screens are NOT a separate fetch: the origin
 * already ships both on every row — `oracleInfo.feeds[]` (the oracle-risk
 * classification) and `risk.breakdown[]` (the governance screen under
 * `category: 'governance'`, the asset screen under `category: 'token'`). So
 * the join is local to the group being stamped, with no extra round-trip and
 * no cross-service dependency.
 *
 * The per-exposure enrichment falls out of the same data: an exposure item
 * points at a SIBLING row's `marketUid`, and that sibling is already in this
 * group — so its oracle and its asset quality are right there.
 */
export function enrichmentIndexFromRows(
  rows: Record<string, any>[],
): EnrichmentIndex {
  const oracleByMarketUid = new Map<string, OracleRiskRow>()
  const governanceByMarketUid = new Map<string, GovernanceRow>()
  const assetRisk: AssetRiskIndex = {}

  for (const row of rows) {
    const uid = String(row?.marketUid ?? '')
    if (!uid) continue

    // --- oracle -----------------------------------------------------------
    const feeds: any[] = Array.isArray(row?.oracleInfo?.feeds)
      ? row.oracleInfo.feeds
      : []
    if (feeds.length > 0) {
      // Singular by construction at `marketUid` granularity — the array only
      // exists because `oracleInfo` also hangs off coarser levels. If a row
      // ever does carry several, take the WORST as primary so the headline
      // never under-reports, and keep the rest as `components`.
      const primary = feeds.reduce((worst, f) =>
        BAND_ORDER.indexOf(String(f?.band)) >
        BAND_ORDER.indexOf(String(worst?.band))
          ? f
          : worst,
      )
      oracleByMarketUid.set(uid, {
        marketUid: uid,
        oracle: primary?.oracle ?? undefined,
        provider: primary?.provider ?? undefined,
        priceDescription: primary?.priceDescription ?? undefined,
        intendedPair: primary?.intendedPair ?? undefined,
        correctOracle: primary?.correctOracle ?? undefined,
        denominatorMatch: primary?.denominatorMatch ?? undefined,
        fixedRate: primary?.fixedRate ?? undefined,
        score: primary?.score ?? row?.oracleInfo?.worstScore,
        band: primary?.band ?? row?.oracleInfo?.worstBand,
        flags: Array.isArray(primary?.flags) ? primary.flags : undefined,
        components:
          feeds.length > 1
            ? feeds.map((f) => String(f?.oracle ?? '')).filter(Boolean)
            : undefined,
      })
    }

    // --- governance + asset quality --------------------------------------
    const breakdown: any[] = Array.isArray(row?.risk?.breakdown)
      ? row.risk.breakdown
      : []
    const gov = breakdown.find((b) => b?.category === 'governance')
    if (gov) {
      governanceByMarketUid.set(uid, {
        marketUid: uid,
        tier: gov.tier ?? undefined,
        score: gov.score ?? undefined,
        ownerKind: gov.ownerKind ?? undefined,
        signerThreshold: gov.signerThreshold ?? null,
        signerCount: gov.signerCount ?? null,
        mode: gov.mode ?? undefined,
        // The screen resolves the governance ROOT but does not carry the
        // timelock delay through to this shape, so the notice period stays
        // genuinely unknown — see `coverage.pending`.
        delaySeconds: gov.delaySeconds ?? null,
      })
    }

    const token = breakdown.find((b) => b?.category === 'token')
    const chainId = String(row?.chainId ?? uid.split(':')[1] ?? '')
    const address = String(
      row?.asset?.address ?? row?.underlying ?? uid.split(':')[2] ?? '',
    ).toLowerCase()
    if (token?.score != null && chainId && address) {
      assetRisk[chainId] ??= {}
      assetRisk[chainId][address] = {
        riskScore: Number(token.score),
        source: token.label ?? undefined,
      }
    }
  }

  return { oracleByMarketUid, governanceByMarketUid, assetRisk }
}

/**
 * Collapse a sheet to its digest form (`?terms=digest`).
 *
 * Drops `items[]` from the exposure sets and the long prose — an Aave market
 * with 30 accepted collaterals is several kB on its own, and it is the SAME
 * accepted set repeated on every row of that lender. Every dropped item is
 * still reachable: each carries a `marketUid` for the bulk endpoint.
 */
export function toDigest(sheet: TermSheet): TermSheetDigest {
  const strip = <T extends { items: unknown[] }>(e: T | undefined) =>
    e ? (({ items, ...rest }) => rest)(e) : undefined

  return {
    schemaVersion: sheet.schemaVersion,
    profileId: sheet.profileId,
    marketUid: sheet.marketUid,
    supply: sheet.supply
      ? {
          rateKind: sheet.supply.rate.kind,
          aprTotal: sheet.supply.rate.aprTotal,
          maturityKind: sheet.supply.maturity.kind,
          maturity: sheet.supply.maturity.maturity,
          exitMode: sheet.supply.exit.mode,
          settlement: sheet.supply.exit.settlement,
          canOpen: sheet.supply.availability.canOpen,
          headline: sheet.supply.info.headline,
          tags: sheet.supply.info.tags,
          backedBy: strip(sheet.supply.backedBy),
        }
      : undefined,
    borrow: sheet.borrow
      ? {
          rateKind: sheet.borrow.rate.kind,
          apr: sheet.borrow.rate.apr,
          maturityKind: sheet.borrow.maturity.kind,
          maturity: sheet.borrow.maturity.maturity,
          debtShape: sheet.borrow.debtShape,
          earlyRepay: sheet.borrow.exit.earlyRepay,
          liquidationTrigger: sheet.borrow.liquidation.trigger,
          canOpen: sheet.borrow.availability.canOpen,
          headline: sheet.borrow.info.headline,
          tags: sheet.borrow.info.tags,
          acceptedCollateral: strip(sheet.borrow.acceptedCollateral),
        }
      : undefined,
    oracle: sheet.oracle
      ? {
          kind: sheet.oracle.kind,
          address: sheet.oracle.address,
          provider: sheet.oracle.provider,
          band: sheet.oracle.band,
        }
      : undefined,
    governance: sheet.governance
      ? {
          mutability: sheet.governance.mutability,
          controllerKind: sheet.governance.controllerKind,
          timelockSecs: sheet.governance.timelockSecs,
          tier: sheet.governance.tier,
        }
      : undefined,
    utilization: sheet.utilization?.utilization,
  }
}

// ---------------------------------------------------------------------------
// Join-time enrichment (governance + oracle + asset quality)
// ---------------------------------------------------------------------------

/** Row shape of `~/risk-data/data/oracles/oracle-risk-flat.json`. */
export interface OracleRiskRow {
  marketUid: string
  oracle?: string
  provider?: string
  priceDescription?: string
  intendedPair?: string
  correctOracle?: boolean | null
  denominatorMatch?: boolean | null
  fixedRate?: boolean
  score?: number
  band?: string
  flags?: string[]
  /** Underlying feeds when an adapter composes several. `oracle` stays the
   *  single source of truth — this is for auditability only. */
  components?: string[]
}

/** Row shape of `~/risk-data/data/lending/market-governance-flat.json`. */
export interface GovernanceRow {
  marketUid: string
  tier?: string
  score?: number
  ownerKind?: string
  signerThreshold?: number | null
  signerCount?: number | null
  mode?: string
  /** Present once the flat builder carries it through (see TERM_SHEET_PLAN §5.4.1). */
  delaySeconds?: number | null
}

/** Per-asset quality, keyed `chainId → lowercased address`. */
export type AssetRiskIndex = Record<
  string,
  Record<
    string,
    {
      riskScore?: number
      source?: string
      liquidityUsd?: number
      governanceScore?: number
      governanceLevel?: string
      upgradeable?: boolean
      canPause?: boolean
      adminKind?: string
    }
  >
>

export interface EnrichmentIndex {
  oracleByMarketUid?: Map<string, OracleRiskRow>
  governanceByMarketUid?: Map<string, GovernanceRow>
  assetRisk?: AssetRiskIndex
}

/**
 * Merge governance / oracle / asset-quality onto a sheet at the SERVING layer.
 *
 * These cannot be computed in-package — they come from the risk-data
 * screeners, which key on the same `marketUid` grammar (a dictionary lookup,
 * not a fuzzy match). `margin-fetcher` emits the sheet with these blocks
 * absent; the worker fills them in, exactly as it already does for
 * `oracleInfo`.
 */
export function enrichTermSheet(
  sheet: TermSheet,
  index: EnrichmentIndex,
): TermSheet {
  const uid = sheet.marketUid
  if (!uid) return sheet

  const oracleRow = index.oracleByMarketUid?.get(uid)
  // An adapter-set `kind: 'none'` (Teller) is a FACT and must win over a
  // missing classification row.
  if (oracleRow?.oracle && sheet.oracle?.kind !== 'none') {
    sheet.oracle = {
      ...sheet.oracle,
      kind: 'price-feed',
      address: oracleRow.oracle.toLowerCase(),
      provider: oracleRow.provider,
      priceDescription: oracleRow.priceDescription,
      intendedPair: oracleRow.intendedPair,
      correctAsset: oracleRow.correctOracle ?? undefined,
      correctNumeraire: oracleRow.denominatorMatch ?? undefined,
      fixedRate: oracleRow.fixedRate,
      score: oracleRow.score,
      band: oracleRow.band,
      flags: oracleRow.flags,
      components: oracleRow.components,
    }
  }

  const govRow = index.governanceByMarketUid?.get(uid)
  if (govRow) {
    // An adapter-asserted `immutable` is a FACT about the protocol's code and
    // must win over the screener, exactly like `oracle.kind: 'none'` above.
    //
    // The screener reads the OWNER of the protocol, so on Morpho Blue it finds
    // a governance root and concludes "governed" — while a Blue market's
    // parameters have no setter at all. Letting the screen overwrite the
    // adapter turned the one lender family designed around immutability into a
    // "parameters can change with no notice" warning.
    const assertedImmutable = sheet.governance?.mutability === 'immutable'
    const immutable = govRow.mode === 'immutable' || assertedImmutable
    const adapterPowers = assertedImmutable
      ? sheet.governance?.powers
      : undefined
    sheet.governance = {
      ...sheet.governance,
      mutability: immutable ? 'immutable' : 'governed',
      controllerKind: govRow.ownerKind,
      safe:
        govRow.signerThreshold != null && govRow.signerCount != null
          ? { threshold: govRow.signerThreshold, owners: govRow.signerCount }
          : undefined,
      // Only meaningful on a TIMELOCK root — see the validator rule.
      timelockSecs:
        govRow.ownerKind === 'TIMELOCK' && govRow.delaySeconds != null
          ? govRow.delaySeconds
          : undefined,
      timelockSource: govRow.delaySeconds != null ? 'screened' : undefined,
      // Three states, not two. A TIMELOCK root whose delay we could not read
      // is NOT the same as "no notice period" — claiming the latter would be
      // a false alarm on the safest governance shape we screen for.
      timelockUnknown:
        govRow.ownerKind === 'TIMELOCK' && govRow.delaySeconds == null
          ? true
          : undefined,
      tier: govRow.tier,
      score: govRow.score,
    }
    if (immutable) {
      // A delay is meaningless where there is nothing to delay.
      sheet.governance.timelockSecs = undefined
      sheet.governance.timelockUnknown = undefined
      // Keep an adapter's narrow, verified power list (Morpho Blue's `setFee`
      // is real and reaches a live market); only DISCARD a screener-derived one,
      // which describes the owner's reach over the protocol rather than over
      // this market.
      sheet.governance.powers = adapterPowers
    }
  }

  // Per-exposure oracle + quality.
  const quality = index.assetRisk
  for (const exp of [
    sheet.supply?.backedBy,
    sheet.borrow?.acceptedCollateral,
  ]) {
    if (!exp) continue
    for (const item of exp.items) {
      const chain = item.asset.chainId
      const addr = item.asset.address
      const q = quality?.[chain]?.[addr]
      if (q) {
        item.quality = {
          riskScore: q.riskScore,
          source: q.source,
          liquidityUsd: q.liquidityUsd,
          governanceScore: q.governanceScore,
          governanceLevel: q.governanceLevel,
          upgradeable: q.upgradeable,
          canPause: q.canPause,
          adminKind: q.adminKind,
        }
      }
      const itemOracle = item.marketUid
        ? index.oracleByMarketUid?.get(item.marketUid)
        : undefined
      if (itemOracle?.oracle) {
        item.oracle = {
          kind: 'price-feed',
          address: itemOracle.oracle.toLowerCase(),
          provider: itemOracle.provider,
          band: itemOracle.band,
          score: itemOracle.score,
          flags: itemOracle.flags,
        }
      }
    }

    // Roll up AFTER enrichment so the digest matches the items.
    const scores = exp.items
      .map((i) => i.quality?.riskScore)
      .filter((n): n is number => n != null)
    if (scores.length) exp.worstRiskScore = Math.max(...scores)

    const BAND_ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
    const bands = exp.items
      .map((i) => i.oracle?.band)
      .filter((b): b is string => !!b)
    if (bands.length) {
      exp.worstOracleBand = bands.reduce((worst, b) =>
        BAND_ORDER.indexOf(b) > BAND_ORDER.indexOf(worst) ? b : worst,
      )
    }
  }

  // Coverage moves from `pending` to `present` for whatever actually landed.
  if (sheet.coverage) {
    const present = new Set(sheet.coverage.present)
    const pending = { ...(sheet.coverage.pending ?? {}) }
    if (sheet.oracle) {
      present.add('oracle')
      delete pending.oracle
    }
    if (sheet.governance) {
      present.add('governance')
      delete pending.governance
      if (sheet.governance.timelockUnknown)
        pending['governance.timelockSecs'] = 'delay-not-carried-by-screen'
    }
    sheet.coverage = {
      ...sheet.coverage,
      present: [...present],
      pending: Object.keys(pending).length ? pending : undefined,
    }
  }

  // Tags are DERIVED, so they must be recomputed now that `governance` and
  // `oracle` exist — otherwise `eoa-controlled`, `no-timelock` and
  // `oracle-flagged` would never appear, and any consumer that ranks severity
  // off tags (the portal does) would silently under-report those markets.
  // `finalizeInfo` preserves an already-written headline/description.
  finalizeInfo(sheet)
  attachImplications(sheet)
  return sheet
}
